import { Avatar, Modal } from "@oysterhr/ui"
import { titleize } from "~/app/utils/stringHelpers"
import { fullDateTime, toLocalTimezone } from "@oysterhr/common"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { BenefitsEnrollment, EngagementType, Maybe } from "@oysterhr/schema"
import { staffContractorPath, staffEngagementPath } from "@oysterhr/router"
import { useEnrollmentState } from "./EnrollmentStatePill/EnrollmentStatePill"

const DataEntry = React.memo((props: React.PropsWithChildren<{ label: string }>) => {
  return (
    <div className="flex flex-col font-medium">
      <div className="text-s text-gray-400">{props.label}</div>
      <div>{props.children}</div>
    </div>
  )
})

export interface EnrollmentInfoModalProps {
  enrollment?: Maybe<BenefitsEnrollment>
  onModalClose: () => void
}

export function EnrollmentInfoModal({ enrollment, onModalClose }: EnrollmentInfoModalProps) {
  const [t] = useTranslation("benefits/EnrollmentState")
  const state = useEnrollmentState(enrollment)
  if (!enrollment) {
    return null
  }

  const isContractor = enrollment.engagement.engagementType === EngagementType.Contract
  const linkToBenefits = `${
    isContractor
      ? staffContractorPath(enrollment.company.id, enrollment.engagement.id)
      : staffEngagementPath(enrollment.company.id, enrollment.engagement.id)
  }#benefits`

  return (
    <Modal data-testid="info-modal" isOpen={!!enrollment} onRequestClose={onModalClose}>
      <div className="p-3">
        <div className="mb-6 w-full overflow-hidden text-center">
          <div className="mb-2 flex w-full justify-center">
            <Avatar
              size="lg"
              name={enrollment.engagement.teamMemberName}
              src={enrollment.engagement.teamMember?.avatarUrl}
            />
          </div>
          <Link to={linkToBenefits} className="text-lg font-medium text-gray-400">
            {enrollment.engagement.teamMemberName}
          </Link>
        </div>
        <div className="-ml-24 flex flex-wrap">
          <div className="ml-24 flex min-w-40 max-w-xs flex-col gap-4">
            <DataEntry label="Provider">{titleize(enrollment.plan.provider.name)}</DataEntry>
            <DataEntry label="Price">
              {enrollment.totalPriceInvoicedCompany?.formatted || "N/A"}
            </DataEntry>
            <DataEntry label="Company">{enrollment.company.name}</DataEntry>
            <DataEntry label="External id">{enrollment.externalId || "N/A"}</DataEntry>
            {enrollment.coverageEndDate && (
              <DataEntry label="End date">
                {fullDateTime(toLocalTimezone(enrollment.coverageEndDate))}
              </DataEntry>
            )}
          </div>
          <div className="ml-24 flex max-w-xs flex-col gap-4">
            <DataEntry label="Plan">{titleize(enrollment.plan.name)}</DataEntry>
            <DataEntry label="State">{t(`states.${state}`)}</DataEntry>
            <DataEntry label="Covered from">
              {fullDateTime(toLocalTimezone(enrollment.coverageStartDate))}
            </DataEntry>
            {enrollment.cancellationReason && (
              <DataEntry label="Termination reason">
                {t(`cancellationReasons.${enrollment.cancellationReason}`, {
                  ns: "benefits/CancellationReasonForm",
                })}
              </DataEntry>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
